import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { PositionIdSchema, SubscriptionIdSchema } from '../primitives/ids';
import { MoneySchema } from '../primitives/money';
import { ReasonCodeSchema } from '../reason-code';

/** Interface languages (FXM-01 §7). */
export const LanguageSchema = z.enum(['fr', 'en']);
/** Language. */
export type Language = z.infer<typeof LanguageSchema>;

/** Trader preferences. */
export const PreferencesSchema = z.object({
  language: LanguageSchema.nullable().describe(
    'null: follow the Telegram app language (FXM-01 §7). A forced choice depends on Q26.',
  ),
});
/** Preferences. */
export type Preferences = z.infer<typeof PreferencesSchema>;

/** Body of PUT /me/preferences. */
export const UpdatePreferencesRequestSchema = z.strictObject({
  language: LanguageSchema.nullable(),
});

// Everything below mirrors specs/M1-home/spec.md §5.1, which is authoritative for GET /me.
// Do not rename a field or a value here without changing M1 first.

/** What an Incomplete account still misses, in onboarding order (FXM-01 §5, M1 R3). */
export const MissingStepSchema = z.enum([
  'telegram_login',
  'choose_channel',
  'connect_trading_account',
  'configure_channel',
]);
/** Missing onboarding step. */
export type MissingStep = z.infer<typeof MissingStepSchema>;

/** One reason the account is Blocked, with when copying stopped and what to do (FXM-01 §6 case 6, M1 R6). */
export const BlockedCauseSchema = z.object({
  reasonCode: ReasonCodeSchema.extract(['TELEGRAM_SESSION_LOST', 'TRADING_ACCOUNT_UNREACHABLE']),
  since: IsoDateTimeSchema,
  action: z.enum(['reidentify_telegram', 'reconnect_trading_account']),
});
/** Blocking cause. */
export type BlockedCause = z.infer<typeof BlockedCauseSchema>;

/** Global status, computed server side (FXM-01 §5; precedence M1 R7, provisional). */
export const AccountStatusSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('incomplete'), missingSteps: z.array(MissingStepSchema).min(1) }),
  z.object({ status: z.literal('operational') }),
  z.object({ status: z.literal('suspended'), reason: z.literal('all_channels_paused') }),
  z.object({ status: z.literal('blocked'), causes: z.array(BlockedCauseSchema).min(1) }),
]);
/** Account status. */
export type AccountStatus = z.infer<typeof AccountStatusSchema>;

/** Copy state of one chosen channel on Home (FXM-02 §5, M1 R8). */
export const ChannelCopyStateSchema = z.enum([
  'active',
  'paused',
  'to_configure',
  'suspended_limit',
  'suspended_external',
]);
/** Channel copy state. */
export type ChannelCopyState = z.infer<typeof ChannelCopyStateSchema>;

/** Summary of a channel's validated settings, as Home shows it (mockup 1). */
export const ChannelSettingSummarySchema = z.object({
  sizing: z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('risk_percent'), riskBps: z.int().min(0) }),
    z.object({ mode: z.literal('fixed_lot'), lots: z.number().positive() }),
  ]),
  executionMode: z.enum(['automatic', 'confirmation']),
});
/** Channel setting summary. */
export type ChannelSettingSummary = z.infer<typeof ChannelSettingSummarySchema>;

/** A chosen channel on Home. `setting` is null when the channel is to configure or was never set up. */
export const HomeChannelSchema = z.object({
  subscriptionId: SubscriptionIdSchema,
  channelName: z.string().min(1).max(256),
  copyState: ChannelCopyStateSchema,
  setting: ChannelSettingSummarySchema.nullable(),
});
/** Home channel. */
export type HomeChannel = z.infer<typeof HomeChannelSchema>;

/** One open FXMate position in the Home preview (M1 R14). */
export const OpenPositionSummarySchema = z.object({
  positionId: PositionIdSchema,
  side: z.enum(['buy', 'sell']),
  instrument: z.string().min(1),
  volumeLots: z.number().positive(),
  channelName: z.string().min(1).max(256),
  profit: MoneySchema,
});
/** Open position summary. */
export type OpenPositionSummary = z.infer<typeof OpenPositionSummarySchema>;

/** Daily limit state: approached from half the limit, reached at the limit (M1 R9). */
export const DailyLossStateSchema = z.enum(['below', 'approached', 'reached']);
/** Daily loss state. */
export type DailyLossState = z.infer<typeof DailyLossStateSchema>;

/** Today's loss against the daily limit. Basis and base provisional until Q1, Q19, Q23. */
export const DailyLossSchema = z.object({
  lossToday: MoneySchema.describe('Loss of the day, in the account currency (Q27).'),
  lossBps: z.int().min(0).describe('floor(loss × 10 000 / base), integer basis points (M1 R13).'),
  limit: z
    .object({ limitBps: z.int().min(1), state: DailyLossStateSchema })
    .nullable()
    .describe('null when no limit is set (I7).'),
  resetsAt: IsoDateTimeSchema.describe('Next midnight Europe/Paris, in UTC.'),
});
/** Daily loss. */
export type DailyLoss = z.infer<typeof DailyLossSchema>;

/** Suggestion to switch a channel to automatic mode after 14 days without an answer (FXM-02 §6 case 11). */
export const AutomaticModeSuggestionSchema = z.object({
  subscriptionId: SubscriptionIdSchema,
  channelName: z.string().min(1).max(256),
});
/** Automatic mode suggestion. */
export type AutomaticModeSuggestion = z.infer<typeof AutomaticModeSuggestionSchema>;

/** The trading account as Home shows it: no account number, no password (I9). */
export const HomeTradingAccountSchema = z.object({
  platform: z.enum(['MT4', 'MT5']),
  server: z.string().min(1).max(128),
  isDemo: z.boolean(),
  connection: z.enum(['reachable', 'unreachable']),
  equity: MoneySchema.nullable(),
});

/** Response of GET /me, polled every 5 s while visible (master X8, M7). */
export const MeResponseSchema = z.object({
  status: AccountStatusSchema,
  tradingAccount: HomeTradingAccountSchema.nullable(),
  channels: z.array(HomeChannelSchema),
  openPositions: z.object({
    count: z.int().min(0),
    preview: z.array(OpenPositionSummarySchema).max(3),
    riskAtStopTotal: MoneySchema.nullable().describe(
      'Sum of riskAtStop over the counted FXMate positions with a stop loss (P10 R6). null when ' +
        'any of them has no known risk: never a partial sum (Q259, provisional).',
    ),
    withoutStopCount: z
      .int()
      .min(0)
      .describe('Counted FXMate positions without a stop loss, left out of the total (P10 R7).'),
  }),
  dailyLoss: DailyLossSchema.nullable().describe('null unless the account is reachable.'),
  automaticModeSuggestions: z.array(AutomaticModeSuggestionSchema),
  generatedAt: IsoDateTimeSchema.describe('When the server computed this snapshot.'),
});
/** GET /me response. */
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** GET /me. */
export const getMeRoute = defineRoute({
  operationId: 'getMe',
  method: 'GET',
  path: '/me',
  tag: 'Account',
  summary: 'Get the global status for Home',
  description:
    'Status with what is missing or blocking, channels and their copy state, open positions preview, daily loss and limit, ' +
    'automatic-mode suggestions (M1 §5.1).',
  source: 'technical spec §6; system design §Mini App; FXM-01 §5; FXM-02 §5; US-09, US-17',
  auth: 'bearer',
  provisional: {
    questions: ['Q1', 'Q19', 'Q25'],
    note: 'daily loss basis and pending validations are open.',
  },
  success: { kind: 'json', status: 200, body: { name: 'MeResponse', schema: MeResponseSchema } },
});

/** PUT /me/preferences. */
export const updatePreferencesRoute = defineRoute({
  operationId: 'updatePreferences',
  method: 'PUT',
  path: '/me/preferences',
  tag: 'Account',
  summary: 'Set the interface language',
  description: 'Stores the language choice; null follows Telegram.',
  source: 'technical spec §6; FXM-01 §7; US-08',
  auth: 'bearer',
  provisional: { questions: ['Q26'], note: "choosing a language against Telegram's is open." },
  body: { name: 'UpdatePreferencesRequest', schema: UpdatePreferencesRequestSchema },
  success: { kind: 'json', status: 200, body: { name: 'Preferences', schema: PreferencesSchema } },
});
